/**
 * StorageService
 * Handles language preference persistence in localStorage
 * Implements IStorageService interface for dependency injection
 */

import type { IStorageService } from '../../types/i18n';
import {
  STORAGE_KEY,
  STORAGE_TEST_KEY,
  SUPPORTED_LANGUAGES,
  MIN_LANGUAGE_CODE_LENGTH,
  MAX_LANGUAGE_CODE_LENGTH,
  STORAGE_VERSION,
  STORAGE_VERSION_KEY,
  AUTO_DETECTED_LANGUAGE_KEY,
} from '../../config/i18n.config';

export class StorageService implements IStorageService {
  /**
   * Save language preference to localStorage
   * @param code Language code to save
   */
  saveLanguage(code: string): void {
    try {
      // Validate input
      if (!code || typeof code !== 'string') {
        console.warn('[Storage] Invalid language code:', code);
        return;
      }

      // Sanitize input (remove whitespace, convert to lowercase)
      const sanitized = code.trim().toLowerCase();

      // Validate it's not empty after sanitization
      if (!sanitized) {
        console.warn('[Storage] Empty language code after sanitization');
        return;
      }

      // Validate length (reasonable language code length)
      if (sanitized.length > MAX_LANGUAGE_CODE_LENGTH) {
        console.warn('[Storage] Language code too long:', sanitized);
        return;
      }

      // Validate it's a supported language
      if (!SUPPORTED_LANGUAGES.includes(sanitized)) {
        console.warn('[Storage] Unsupported language code:', sanitized);
        // Still save it, but warn
      }

      // Check if localStorage is available
      if (!this.isLocalStorageAvailable()) {
        console.warn('[Storage] localStorage is not available');
        return;
      }

      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, sanitized);
    } catch (error) {
      // Handle QuotaExceededError and other storage errors
      if (error instanceof DOMException) {
        console.warn('[Storage] Failed to save language (quota exceeded or disabled):', error);
      } else {
        console.error('[Storage] Unexpected error saving language:', error);
      }
      // Don't throw - fail gracefully
    }
  }

  /**
   * Retrieve language preference from localStorage
   * @returns Language code or null if not found
   */
  getLanguage(): string | null {
    try {
      // Check if localStorage is available
      if (!this.isLocalStorageAvailable()) {
        return null;
      }

      // Retrieve from localStorage
      const stored = localStorage.getItem(STORAGE_KEY);

      // Return null if nothing stored
      if (!stored) {
        return null;
      }

      // Sanitize retrieved value
      const sanitized = stored.trim().toLowerCase();

      // Validate it's not empty
      if (!sanitized) {
        return null;
      }

      // Validate it looks like a valid language code
      if (
        sanitized.length < MIN_LANGUAGE_CODE_LENGTH ||
        sanitized.length > MAX_LANGUAGE_CODE_LENGTH
      ) {
        console.warn('[Storage] Invalid stored language code:', sanitized);
        return null;
      }

      return sanitized;
    } catch (error) {
      console.warn('[Storage] Error retrieving language:', error);
      return null;
    }
  }

  /**
   * Clear language preference from localStorage
   */
  clearLanguage(): void {
    try {
      // Check if localStorage is available
      if (!this.isLocalStorageAvailable()) {
        return;
      }

      // Remove from localStorage
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('[Storage] Error clearing language:', error);
      // Don't throw - fail gracefully
    }
  }

  /**
   * Check if a language preference is stored
   * @returns True if language is stored
   */
  hasStoredLanguage(): boolean {
    try {
      // Check if localStorage is available
      if (!this.isLocalStorageAvailable()) {
        return false;
      }

      // Check if key exists and has value
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored !== null && stored.trim().length > 0;
    } catch (error) {
      console.warn('[Storage] Error checking stored language:', error);
      return false;
    }
  }

  /**
   * Save auto-detected language (for showing "Auto-detected: X" message)
   * @param code Language code that was auto-detected
   */
  saveAutoDetectedLanguage(code: string): void {
    try {
      if (!this.isLocalStorageAvailable()) {
        return;
      }

      const sanitized = code.trim().toLowerCase();
      if (sanitized) {
        localStorage.setItem(AUTO_DETECTED_LANGUAGE_KEY, sanitized);
      }
    } catch (error) {
      console.warn('[Storage] Failed to save auto-detected language:', error);
    }
  }

  /**
   * Get auto-detected language
   * @returns Language code or null
   */
  getAutoDetectedLanguage(): string | null {
    try {
      if (!this.isLocalStorageAvailable()) {
        return null;
      }

      return localStorage.getItem(AUTO_DETECTED_LANGUAGE_KEY);
    } catch (error) {
      console.warn('[Storage] Failed to get auto-detected language:', error);
      return null;
    }
  }

  /**
   * Clear auto-detected language
   */
  clearAutoDetectedLanguage(): void {
    try {
      if (!this.isLocalStorageAvailable()) {
        return;
      }

      localStorage.removeItem(AUTO_DETECTED_LANGUAGE_KEY);
    } catch (error) {
      console.warn('[Storage] Failed to clear auto-detected language:', error);
    }
  }

  /**
   * Check and migrate storage if needed
   * @returns True if migration was performed
   */
  checkAndMigrateStorage(): boolean {
    try {
      if (!this.isLocalStorageAvailable()) {
        return false;
      }

      const currentVersion = localStorage.getItem(STORAGE_VERSION_KEY);

      // If no version stored, initialize it
      if (!currentVersion) {
        localStorage.setItem(STORAGE_VERSION_KEY, STORAGE_VERSION);
        return false;
      }

      // If version mismatch, perform migration
      if (currentVersion !== STORAGE_VERSION) {
        console.log('[Storage] Migrating storage from', currentVersion, 'to', STORAGE_VERSION);
        // Future migration logic goes here
        localStorage.setItem(STORAGE_VERSION_KEY, STORAGE_VERSION);
        return true;
      }

      return false;
    } catch (error) {
      console.warn('[Storage] Failed to check/migrate storage:', error);
      return false;
    }
  }

  /**
   * Check if localStorage is available
   * @private
   */
  private isLocalStorageAvailable(): boolean {
    try {
      // Check if localStorage exists
      if (typeof localStorage === 'undefined' || localStorage === null) {
        return false;
      }

      // Test if we can actually use it (some browsers block in private mode)
      localStorage.setItem(STORAGE_TEST_KEY, 'test');
      localStorage.removeItem(STORAGE_TEST_KEY);
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance for convenience (but allow injection for testing)
export const storageService = new StorageService();
